import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, Trash2, Sparkles, AlertTriangle, BookOpen, ScanSearch,
  History, Clock, Download, FileText, Tag as TagIcon, Edit2, Check, X,
} from "lucide-react";
import { useNav, VIEWS } from "@/lib/nav";
import { ruleTypeLabel, ConflictsPanel } from "@/pages/ProposePage";
import { RevisionSuggestion } from "@/components/RevisionSuggestion";
import { buildPlaytestReport, downloadText, slugify } from "@/lib/playtestReport";
import { TagsInput } from "@/components/TagsInput";
import { CalloutFeedbackProvider, feedbackKey } from "@/lib/calloutFeedback";
import { ModelCheckList } from "@/pages/ModelCheckHistory";
import { CommentsPanel } from "@/components/CommentsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

export function HistoryPage() {
  const { navigate } = useNav();
  const [q, setQ] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [data, setData] = useState({ total: 0, items: [], all_tags: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.listProposals({ q, tag: tagFilter, limit: 200 });
      setData(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    const live = setInterval(load, 15000);
    return () => { clearTimeout(t); clearInterval(live); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tagFilter]);

  const onDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this proposal from history?")) return;
    await api.deleteProposal(id);
    load();
  };

  return (
    <div className="grid gap-3" data-testid="history-page">
      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <History className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Proposal history</h2>
            <Badge variant="outline" className="ml-1">{data.total}</Badge>
            <div className="ml-auto flex items-center gap-1">
              <a
                href={api.proposalExportUrl(q, tagFilter)}
                download="proposals.md"
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-border hover:border-primary/60 hover:bg-card/70 transition"
                data-testid="export-md-link"
              >
                <FileText className="w-3.5 h-3.5" /> Markdown bulk export
              </a>
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search past proposals by wording, name, summary, or tag…"
              className="pl-9"
              data-testid="history-search-input"
            />
          </div>
          {data.all_tags.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap" data-testid="history-tag-filters">
              <TagIcon className="w-3.5 h-3.5 text-muted-foreground" />
              <button
                onClick={() => setTagFilter("")}
                className={`text-xs px-2 py-0.5 rounded border transition ${tagFilter === "" ? "border-primary/60 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                data-testid="history-tag-filter-all"
              >all</button>
              {data.all_tags.map((t) => (
                <button
                  key={t}
                  onClick={() => setTagFilter(tagFilter === t ? "" : t)}
                  className={`text-xs px-2 py-0.5 rounded border transition ${tagFilter === t ? "border-primary/60 bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                  data-testid={`history-tag-filter-${t}`}
                >{t}</button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="model" className="w-full">
        <TabsList data-testid="history-tabs">
          <TabsTrigger value="model" data-testid="history-tab-model">Model checks</TabsTrigger>
          <TabsTrigger value="quick" data-testid="history-tab-quick">Quick checks &amp; compares</TabsTrigger>
        </TabsList>
        <TabsContent value="model"><ModelCheckList q={q} /></TabsContent>
        <TabsContent value="quick" className="grid gap-3">
      {loading && (
        <>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </>
      )}

      {!loading && data.items.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No saved proposals match.
          </CardContent>
        </Card>
      )}

      <ScrollArea className="max-h-[70vh]">
        <div className="space-y-2 pr-2" data-testid="history-list">
          {data.items.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer transition hover:border-primary/50 hover:bg-card/80"
              onClick={() => navigate(VIEWS.historyDetail(p.id))}
              data-testid={`history-row-${p.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground flex-wrap">
                      {p.name && <span className="text-sm font-semibold text-foreground">{p.name}</span>}
                      <Clock className="w-3 h-3" />
                      <span>{p.created_by?.name ? `${p.created_by.name} · ` : ""}{fmtDate(p.created_at)}</span>
                      {p.has_alternative && (
                        <Badge variant="outline" className="text-cyan-400 border-cyan-700/60">A vs B</Badge>
                      )}
                      {p.rule_type && (
                        <Badge
                          variant="outline"
                          className="text-violet-300 border-violet-700/60 bg-violet-950/30"
                          data-testid={`history-rule-type-${p.rule_type}`}
                        >
                          {ruleTypeLabel(p.rule_type)}
                        </Badge>
                      )}
                      {(p.tags || []).map((t) => (
                        <Badge key={t} variant="outline" className="text-primary border-primary/40 bg-primary/10">{t}</Badge>
                      ))}
                    </div>
                    <p className="text-sm leading-relaxed line-clamp-2 mb-2">{p.proposed_text}</p>
                    {p.summary && (
                      <p className="text-xs text-muted-foreground line-clamp-2 italic">{p.summary}</p>
                    )}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-amber-400 border-amber-700/40">
                        <Sparkles className="w-3 h-3 mr-1" /> {p.semantic_count} semantic
                      </Badge>
                      <Badge variant="outline" className="text-cyan-400 border-cyan-700/40">
                        <BookOpen className="w-3 h-3 mr-1" /> {p.fast_count} textual
                      </Badge>
                      <Badge variant="outline" className={p.conflict_count > 0 ? "text-rose-400 border-rose-700/40" : "text-muted-foreground"}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> {p.conflict_count} conflicts
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-rose-400 -mt-1 -mr-1"
                    onClick={(e) => onDelete(p.id, e)}
                    data-testid={`history-delete-${p.id}`}
                    aria-label="Delete proposal"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function HistoryDetail({ id }) {
  const { navigate } = useNav();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [tags, setTags] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.getProposal(id)
      .then((d) => {
        setProposal(d);
        setName(d.name || "");
        setTags(d.tags || []);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const saveEdits = async () => {
    const updated = await api.updateProposal(id, { name, tags });
    setProposal(updated);
    setEditing(false);
  };

  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!proposal) return <p>Proposal not found.</p>;

  const r = proposal.result || {};
  const rb = proposal.alternative_result;

  return (
    <div className="space-y-4" data-testid="history-detail">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <History className="w-5 h-5 text-primary" /> Saved proposal
            </h2>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-muted-foreground">
                {fmtDate(proposal.created_at)} · snapshot {proposal.snapshot_id} · {proposal.llm_model}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                data-testid="export-playtest-report-btn"
                onClick={() => {
                  const report = buildPlaytestReport(proposal);
                  const filename = `playtest-${slugify(proposal.name || proposal.id)}-${new Date().toISOString().slice(0, 10)}.txt`;
                  downloadText(filename, report);
                }}
              >
                <Download className="w-3.5 h-3.5" /> Export playtest report
              </Button>
            </div>
          </div>

          {/* Name + tags editor */}
          <div className="mb-4 p-3 rounded border border-border bg-background/40">
            {editing ? (
              <div className="space-y-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Proposal name (optional)"
                  data-testid="edit-name-input"
                />
                <TagsInput value={tags} onChange={setTags} testId="edit-tags" placeholder="Add tag and press Enter…" />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={saveEdits} data-testid="save-edits-btn"><Check className="w-3.5 h-3.5 mr-1" /> Save</Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(false); setName(proposal.name || ""); setTags(proposal.tags || []); }} data-testid="cancel-edits-btn"><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <strong className="text-sm">{proposal.name || <span className="text-muted-foreground italic">Untitled</span>}</strong>
                {proposal.rule_type && (
                  <Badge
                    variant="outline"
                    className="text-violet-300 border-violet-700/60 bg-violet-950/30"
                    data-testid={`detail-rule-type-${proposal.rule_type}`}
                  >
                    {ruleTypeLabel(proposal.rule_type)}
                  </Badge>
                )}
                {(proposal.tags || []).map((t) => (
                  <Badge key={t} variant="outline" className="text-primary border-primary/40 bg-primary/10">{t}</Badge>
                ))}
                {(proposal.tags || []).length === 0 && (
                  <span className="text-xs text-muted-foreground italic">no tags</span>
                )}
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setEditing(true)} data-testid="edit-proposal-btn">
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit name / tags
                </Button>
              </div>
            )}
          </div>

          <div className={rb ? "grid md:grid-cols-2 gap-4" : ""}>
            <ProposalPanel label="Proposed wording" text={proposal.proposed_text} result={r} navigate={navigate} proposal={proposal} />
            {rb && (
              <ProposalPanel label="Alternative wording" text={proposal.alternative_text} result={rb} navigate={navigate} />
            )}
          </div>
          {proposal.comparison?.summary && (
            <div className="mt-4 p-3 rounded border border-primary/30 bg-primary/5">
              <h4 className="text-sm font-medium mb-1 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary" /> Comparison
              </h4>
              <p className="text-sm leading-relaxed">{proposal.comparison.summary}</p>
            </div>
          )}
        </CardContent>
      </Card>
      {proposal && <CommentsPanel targetType="proposal" targetId={proposal.id} />}
    </div>
  );
}

function ProposalPanel({ label, text, result, navigate, proposal }) {
  return (
    <div className="space-y-3" data-testid="proposal-panel">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
        <p className="text-sm leading-relaxed bg-background/40 rounded p-3 border border-border">
          {text}
        </p>
      </div>
      {result.summary && (
        <div className="text-sm leading-relaxed">
          <strong className="text-primary">QA Summary:</strong> {result.summary}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        <Badge variant="outline" className="text-amber-400 border-amber-700/40">
          <Sparkles className="w-3 h-3 mr-1" /> {(result.semantic_similar || []).length} semantic
        </Badge>
        <Badge variant="outline" className="text-cyan-400 border-cyan-700/40">
          <ScanSearch className="w-3 h-3 mr-1" /> {(result.fast_similar || []).length} textual
        </Badge>
        <Badge variant="outline" className={(result.conflicts || []).length > 0 ? "text-rose-400 border-rose-700/40" : "text-muted-foreground"}>
          <AlertTriangle className="w-3 h-3 mr-1" /> {(result.conflicts || []).length} conflicts
        </Badge>
      </div>
      <CalloutFeedbackProvider
        scopeText={text}
        ruleType={proposal?.rule_type || result.rule_type}
        keys={(result.conflicts || []).map(feedbackKey.conflict)}
      >
      <ConflictsPanel conflicts={result.conflicts || []} />

      {proposal && (
        <RevisionSuggestion
          proposedText={proposal.proposed_text}
          ruleType={proposal.rule_type || result.rule_type}
          modelTypes={proposal.model_types || result.model_types || []}
          conflicts={result.conflicts || []}
          existing={result.revision_suggestion}
          savedId={proposal.id}
          onSave={(rev) => {
            if (result) result.revision_suggestion = rev;
          }}
        />
      )}
      </CalloutFeedbackProvider>
    </div>
  );
}
